/**
 * Освоенный объём (ПЛ-19): PV, EV, AC по основному базовому плану, SPI и CPI, прогноз затрат тремя способами,
 * прогноз окончания, разрез и S-кривые. Только с уровнем денег «Акты».
 */
import type { EarnedValue, ScheduleView } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { api } from '../../lib/api';
import { tenge } from '../../lib/format';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { LField } from './LField';

const GROUPS = {
  section: 'По разделам ИСР',
  contractor: 'По подрядчикам',
  workType: 'По видам работ',
} as const;
const k = (x: number | null) => (x == null ? '—' : String(x).replace('.', ','));
const dd = (s: string | null) => (s ? s.slice(0, 10).split('-').reverse().join('.') : '—');
const money = (x: number | null) => (x == null ? '—' : tenge(x));

export function EvPanel({ view }: { view: ScheduleView }) {
  const [groupBy, setGroupBy] = useState<keyof typeof GROUPS>('section');
  const q = useQuery({
    queryKey: ['ev', view.schedule.id, groupBy, view.schedule.revision],
    queryFn: () => api<EarnedValue>(`/schedules/${view.schedule.id}/earned-value`, { query: { groupBy } }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const e = q.data;
  if (!e.baseline || !e.total.bac)
    return (
      <EmptyState title="Нет бюджета для освоенного объёма">
        Нужен основной базовый план со стоимостью работ: назначьте людей, технику и материалы со ставками и
        сохраните базовый план.
      </EmptyState>
    );
  const t = e.total;
  const max = Math.max(1, ...e.curve.map((c) => Math.max(c.pv, c.ev ?? 0, c.ac ?? 0)));
  const W = Math.max(1, e.curve.length - 1);
  const path = (get: (c: EarnedValue['curve'][number]) => number | null) =>
    e.curve
      .map((c, i) => [i, get(c)] as const)
      .filter(([, v]) => v != null)
      .map(([i, v], j) => `${j ? 'L' : 'M'}${(i / W) * 1000},${100 - ((v as number) / max) * 96}`)
      .join(' ');
  return (
    <div className="flex flex-col gap-4">
      <Panel title="Освоенный объём" meta={`${e.baseline.name} · на ${dd(e.date)}`}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {(
            [
              ['Бюджет (BAC)', money(t.bac)],
              ['План на дату (PV)', money(t.pv)],
              ['Освоено (EV)', money(t.ev)],
              ['Факт затрат (AC)', money(t.ac)],
              ['SPI — по срокам', k(t.spi)],
              ['CPI — по затратам', k(t.cpi)],
              ['Отклонение по срокам (SV)', money(t.sv)],
              ['Отклонение по затратам (CV)', money(t.cv)],
            ] as const
          ).map(([title, v]) => (
            <div key={title} className="rounded-md border border-line bg-surface px-3 py-2">
              <div className="text-[12px] text-ink-2">{title}</div>
              <div
                className={clsx(
                  'text-[17px] font-semibold',
                  ((title.startsWith('SPI') && (t.spi ?? 1) < 1) ||
                    (title.startsWith('CPI') && (t.cpi ?? 1) < 1)) &&
                    'text-danger',
                )}
              >
                {v}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[14px]">
          Прогноз затрат на завершение: по CPI <b>{money(e.forecasts.byCpi)}</b> · по CPI и SPI{' '}
          <b>{money(e.forecasts.byCpiSpi)}</b> · по оставшемуся плану <b>{money(e.forecasts.byPlan)}</b>.
          Нужный CPI на остаток (TCPI): {k(t.tcpi)}.
        </p>
        <p className="text-[14px]">
          Окончание по базовому плану {dd(e.finishBaseline)}, прогноз по SPI{' '}
          <b
            className={clsx(
              e.finishForecast && e.finishBaseline && e.finishForecast > e.finishBaseline && 'text-danger',
            )}
          >
            {dd(e.finishForecast)}
          </b>
          .
        </p>
        <p className="mt-1 text-[12px] text-ink-2">
          Факт затрат: {e.acSources.join('; ')}.
          {e.missingRates.length ? ` Нет ставки — факт не учтён: ${e.missingRates.join(', ')}.` : ''}
          {e.withoutCost
            ? ` Работ без стоимости в базовом плане: ${e.withoutCost} — в расчёт не входят.`
            : ''}
        </p>
      </Panel>
      {e.curve.length > 1 ? (
        <Panel title="S-кривые" meta="нарастающим итогом по неделям">
          <svg
            viewBox="0 0 1000 100"
            preserveAspectRatio="none"
            className="h-48 w-full"
            role="img"
            aria-label="Кривые PV, EV и AC"
          >
            <path
              d={path((c) => c.pv)}
              fill="none"
              stroke="var(--color-ink-2)"
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
            />
            <path
              d={path((c) => c.ev)}
              fill="none"
              stroke="var(--color-success)"
              strokeWidth={2.5}
              vectorEffect="non-scaling-stroke"
            />
            <path
              d={path((c) => c.ac)}
              fill="none"
              stroke="var(--color-danger)"
              strokeWidth={2.5}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          <div className="flex flex-wrap justify-between gap-2 text-[12px] text-ink-2">
            <span>{dd(e.curve[0]!.week)}</span>
            <span className="inline-flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-1">
                <span className="inline-block h-0.5 w-4 bg-ink-2" /> план (PV)
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="inline-block h-0.5 w-4 bg-success" /> освоено (EV)
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="inline-block h-0.5 w-4 bg-danger" /> факт (AC)
              </span>
            </span>
            <span>{dd(e.curve.at(-1)!.week)}</span>
          </div>
        </Panel>
      ) : null}
      <Panel
        title={GROUPS[groupBy]}
        actions={
          <LField label="Разрез">
            <Select
              value={groupBy}
              onChange={(x) => setGroupBy(x.target.value as keyof typeof GROUPS)}
              className="max-md:h-11"
            >
              {Object.entries(GROUPS).map(([key, title]) => (
                <option key={key} value={key}>
                  {title}
                </option>
              ))}
            </Select>
          </LField>
        }
      >
        <ScrollX>
          <table className="w-full min-w-[760px] text-[13px]">
            <thead>
              <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                <th className="py-2 pr-2 pl-3">Группа</th>
                <th className="py-2 pr-2 text-right">BAC</th>
                <th className="py-2 pr-2 text-right">PV</th>
                <th className="py-2 pr-2 text-right">EV</th>
                <th className="py-2 pr-2 text-right">AC</th>
                <th className="py-2 pr-2 text-right">SPI</th>
                <th className="py-2 pr-2 text-right">CPI</th>
                <th className="py-2 pr-3 text-right">EAC</th>
              </tr>
            </thead>
            <tbody>
              {e.groups.map((g) => (
                <tr key={g.key || g.name} className="border-t border-line">
                  <td className="py-1.5 pr-2 pl-3">{g.name}</td>
                  <td className="num py-1.5 pr-2 text-right">{money(g.bac)}</td>
                  <td className="num py-1.5 pr-2 text-right">{money(g.pv)}</td>
                  <td className="num py-1.5 pr-2 text-right">{money(g.ev)}</td>
                  <td className="num py-1.5 pr-2 text-right">{money(g.ac)}</td>
                  <td className={clsx('num py-1.5 pr-2 text-right', (g.spi ?? 1) < 1 && 'text-danger')}>
                    {k(g.spi)}
                  </td>
                  <td className={clsx('num py-1.5 pr-2 text-right', (g.cpi ?? 1) < 1 && 'text-danger')}>
                    {k(g.cpi)}
                  </td>
                  <td className="num py-1.5 pr-3 text-right">{money(g.eac)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollX>
      </Panel>
    </div>
  );
}
